import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { referralApi } from '../../api/referralApi'
import { ReferralApplication } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, Briefcase } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { clsx } from 'clsx'

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'badge bg-yellow-50 text-yellow-700',
  REVIEWED: 'badge bg-blue-50 text-blue-700',
  SELECTED: 'badge bg-green-50 text-green-700',
  REJECTED: 'badge bg-red-50 text-red-700',
}

export default function MyApplicationsPage() {
  const [applications, setApplications] = useState<ReferralApplication[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    referralApi
      .getMyApplications()
      .then(({ data }) => setApplications((data.data as any)?.content || data.data || []))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-6">My Applications</h1>

      {applications.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          <Briefcase size={40} className="mx-auto mb-3 opacity-30" />
          <p>No applications yet.</p>
          <Link to="/" className="btn-primary mt-4 inline-block">Browse Jobs</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <div key={app.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                    {app.jobPost.company.logoUrl ? (
                      <img src={app.jobPost.company.logoUrl} alt={app.jobPost.company.name} className="w-full h-full object-cover" />
                    ) : (
                      <Briefcase size={18} className="text-gray-400" />
                    )}
                  </div>
                  <div>
                    <Link
                      to={`/jobs/${app.jobPost.id}`}
                      className="font-semibold text-gray-900 hover:text-primary-600 no-underline"
                    >
                      {app.jobPost.title}
                    </Link>
                    <p className="text-sm text-gray-500">{app.jobPost.company.name}</p>
                  </div>
                </div>
                <span className={clsx(STATUS_STYLE[app.status])}>{app.status}</span>
              </div>

              <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                <span>Applied {formatDistanceToNow(new Date(app.createdAt), { addSuffix: true })}</span>
                {app.feeAmountPaise > 0 && (
                  <span className="text-orange-500">₹{app.feeAmountPaise / 100} in escrow</span>
                )}
              </div>

              {app.status === 'REFERRED' && (
                <Link
                  to={`/referrals/my`}
                  className="btn-primary text-xs mt-3 inline-block"
                >
                  View Referral Details
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
